import { createContext, type ReactNode, useContext, useMemo, useState } from "react"
import type { Translations } from "@/types"

type Interpolations = Record<string, string | number>

export type TranslateFn = (key: string, interpolations?: Interpolations) => string

type I18nContextValue = {
  locale: string
  setLocale: (locale: string) => void
  t: TranslateFn
}

const I18nContext = createContext<I18nContextValue | null>(null)

function lookup(translations: Translations, key: string): string | undefined {
  let node: string | Translations | undefined = translations
  for (const part of key.split(".")) {
    if (typeof node !== "object" || node === null) {
      return undefined
    }
    node = node[part]
  }
  return typeof node === "string" ? node : undefined
}

function interpolate(text: string, interpolations?: Interpolations): string {
  if (!interpolations) {
    return text
  }
  return text.replace(/%\{(\w+)\}/g, (match, name) =>
    name in interpolations ? String(interpolations[name]) : match,
  )
}

export function I18nProvider({
  locale: initialLocale,
  translations,
  children,
}: {
  locale: string
  translations: Translations
  children: ReactNode
}) {
  const [locale, setLocale] = useState(initialLocale)

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, interpolations) => {
        const text = lookup(translations, key)
        return text === undefined ? key : interpolate(text, interpolations)
      },
    }),
    [locale, translations],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useTranslation() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error("useTranslation must be used within an I18nProvider")
  }
  return context
}
